import { Component, inject, output, signal, OnInit, LOCALE_ID } from '@angular/core';
import { optionalName } from '../../../../../../validators/name-rules';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { BundleService } from '../../../../../../services/bundle/bundle';
import { Loading } from '../../../../../../utils/loading/loading';
import { SuccessfulNotification } from '../../../../../../utils/successful-notification/successful-notification';
import { forkJoin, of } from 'rxjs';
import { catchError, map, finalize } from 'rxjs/operators';
import { Error } from '../../../../../public-user-component/auth/shared/error/error';
import { BundleFileMgt } from '../../../../../../services/bundle/bundle.file-mgt.service';

@Component({
  selector: 'app-create-bundle-info-component',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, Loading, SuccessfulNotification, Error],
  templateUrl: './create-bundle-info-component.html',
  styleUrl: './create-bundle-info-component.scss',
})
export class CreateBundleInfoComponent implements OnInit {
  private fb = inject(FormBuilder);
  private bundleService = inject(BundleService);
  private fileMgt = inject(BundleFileMgt);
  private locale = inject(LOCALE_ID);

  onSuccess = output<number>();

  bundleForm!: FormGroup;
  loading = signal(false);
  error = signal<any>(null);
  showSuccess = signal(false);
  selectedImages = signal<{ file: File; preview: string }[]>([]);

  get isArabic() {
    return this.locale.startsWith('ar');
  }

  

  get successTitle() {
    return $localize`:@@bundle.info.success:Bundle information saved successfully`;
  }

  ngOnInit() {
    this.initForm();
  }

  initForm() {
    this.bundleForm = this.fb.group({
      titleEn: ['', [Validators.maxLength(150), this.noArabicValidator]],
      titleAr: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(150), this.arabicOnlyValidator]],
      descriptionEn: ['', [Validators.maxLength(2000), this.noArabicValidator]],
      descriptionAr: ['', [Validators.maxLength(2000), this.arabicOnlyValidator]],
      discountType: ['PERCENTAGE', Validators.required],
      discountValue: [null, [Validators.required, Validators.min(1), Validators.max(100)]],
      startDate: ['', [Validators.required, this.dateNotPastValidator]],
      endDate: ['', Validators.required],
    }, { validators: this.dateRangeValidator });
  }

  noArabicValidator = (control: AbstractControl) => {
    if (!control.value) return null;
    return /[\u0600-\u06FF]/.test(control.value) ? { containsArabic: true } : null;
  };

  arabicOnlyValidator = (control: AbstractControl) => {
    if (!control.value) return null;
    return /[a-zA-Z]/.test(control.value) ? { containsEnglish: true } : null;
  };

  dateNotPastValidator = (control: AbstractControl) => {
    if (!control.value) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(control.value) < today ? { dateInPast: true } : null;
  };

  dateRangeValidator = (group: AbstractControl) => {
    const start = group.get('startDate')?.value;
    const end = group.get('endDate')?.value;
    if (!start || !end) return null;
    return new Date(end) <= new Date(start) ? { dateInvalidRange: true } : null;
  };

  onFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) this.handleFiles(Array.from(input.files));
    input.value = '';
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer?.files) this.handleFiles(Array.from(event.dataTransfer.files));
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  private handleFiles(files: File[]) {
    files.forEach(file => {
      const isFormatValid = ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type);
      if (this.selectedImages().length < 5 && file.size <= 5 * 1024 * 1024 && isFormatValid) {
        const reader = new FileReader();
        reader.onload = (e) => this.selectedImages.update(prev => [...prev, { file, preview: e.target?.result as string }]);
        reader.readAsDataURL(file);
      }
    });
  }

  removeSelectedImage(index: number) {
    this.selectedImages.update(imgs => imgs.filter((_, i) => i !== index));
  }

  onSubmit() {
    if (this.bundleForm.invalid || this.selectedImages().length === 0) {
      return;
    }

    this.loading.set(true);
    const uploads$ = this.selectedImages().map(img => 
      this.fileMgt.uploadImage(img.file).pipe(
        map(res => res.fid),
        catchError(() => of(null))
      )
    );

    forkJoin(uploads$).subscribe(fids => {
      const validFids = fids.filter(f => f !== null) as string[];

      if (validFids.length === 0) {
        this.loading.set(false);
        return;
      }

      const payload = {
        titleEn: optionalName(this.bundleForm.value.titleEn),
        titleAr: this.bundleForm.value.titleAr.trim(),
        descriptionEn: this.bundleForm.value.descriptionEn || null,
        descriptionAr: this.bundleForm.value.descriptionAr || null,
        discountType: this.bundleForm.value.discountType,
        discountValue: Number(this.bundleForm.value.discountValue),
        startDate: this.bundleForm.value.startDate,
        endDate: this.bundleForm.value.endDate,
        imagesFids: validFids,
        profileImageFid: validFids[0]
      };

      this.bundleService.createBundle(payload)
        .pipe(finalize(() => this.loading.set(false)))
        .subscribe({
          next: (res) => {
            this.showSuccess.set(true);
            setTimeout(() => this.onSuccess.emit(res.id), 1000);
          },
          error: (err) => this.error.set(err.error)
        });
    });
  }
}